import { useEffect } from 'react';

import { ColorScheme } from '@mantine/core';
import { Amplify, Hub } from 'aws-amplify';
import NextApp, { AppContext, AppProps } from 'next/app';
// eslint-disable-next-line no-restricted-imports
import { useRouter } from 'next/router';
import Script from 'next/script';

import { GA_MEASUREMENT_ID, pageview } from '@/lib/analytics/gtag';
import { updatedAwsConfig } from '@/lib/auth/auth';
import { listener } from '@/lib/auth/hub-listener';
import { OutputErrorLog } from '@/lib/error';
import { getInitialData } from '@/lib/getIntialData';
import { AppProvider } from '@/providers/app';
import '@/styles/globals.css';
import { PAGE_PATHS } from '@/utils/constant';
import { getIdTokenFromCookies } from '@/utils/getIdTokenFromCookies';
import 'cesium/Build/Cesium/Widgets/widgets.css';

Amplify.configure(updatedAwsConfig);
Hub.listen('auth', listener);
if (typeof window !== 'undefined') {
  // @ts-ignore
  window.CESIUM_BASE_URL = '/cesium';
}
export default function App(
  props: AppProps & {
    colorScheme: ColorScheme;
    fallback: {
      [x: string]: any;
    };
  }
) {
  const { Component, pageProps, fallback } = props;
  const router = useRouter();

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      pageview(url);
    };

    // 初回ページロード時のページビューを送信
    pageview(router.asPath);

    // ルート変更時のページビューを送信
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events, router.asPath]);

  return (
    <>
      {/* Google Analytics */}
      {GA_MEASUREMENT_ID && (
        <>
          <Script
            strategy="afterInteractive"
            src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
          />
          <Script
            id="google-analytics"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${GA_MEASUREMENT_ID}', {
                  page_path: window.location.pathname,
                });
              `,
            }}
          />
        </>
      )}
      <AppProvider fallback={fallback}>
        <Component {...pageProps} />
      </AppProvider>
    </>
  );
}

export type ExtendedAppContext = {
  ctx: {
    req: {
      cookies?: {
        [x: string]: string;
      };
    };
    query: {
      siteId?: string | number;
      userId?: string | number;
      companyId?: string | number;
      finishedShapeId?: string | number;
    };
  };
} & AppContext;

//MEMO getServerSirePropsから呼び出したBFFサーバでCookieを取得できない。
//Clientサイドから呼び出した場合は取得できる。原因はおそらく、baseURLを変えているから。現状は解決策わからない。

App.getInitialProps = async (appContext: ExtendedAppContext) => {
  try {
    //ログインとIndexでは何もしない
    if (
      appContext.router.pathname === PAGE_PATHS.TOP ||
      appContext.router.pathname === PAGE_PATHS.LOGIN ||
      appContext.router.pathname === PAGE_PATHS.SITELINK
    ) {
      return {
        fallback: {},
      };
    }
    const appProps = await NextApp.getInitialProps(appContext);
    const { req } = appContext.ctx;
    if (!req?.cookies)
      return {
        ...appProps,
        fallback: {
          siteList: [],
        },
      };
    const idToken = getIdTokenFromCookies(req.cookies);
    if (!idToken) {
      return {
        ...appProps,
        fallback: {
          siteList: [],
        },
      };
    }
    const fallback = await getInitialData(appContext, idToken);
    return {
      ...appProps,
      fallback,
    };
  } catch (error) {
    OutputErrorLog('getinitialProps', error);
    return {
      fallback: {},
    };
  }
};
